import React, { useState } from 'react';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LockIcon from '@mui/icons-material/Lock';
import { raffleApi } from './raffleApi';
import {
  Notice,
  SectionLabel,
  Sheet,
  SheetTitle,
  Stepper,
  TicketStub,
} from './RafflePieces';

/**
 * Tickets given out — R73–R76. The employee moves the ceiling (the last
 * ticket handed to a customer); the book range, colour and on/off stay with
 * the manager. Values are clamped to the book here and again on the server.
 * Saving from inside a raffle also answers the pre-draw gate (R79).
 */
export default function GivenOutSheet({
  drawing,
  locationId,
  runId,
  onClose,
  onSaved,
}) {
  const books = drawing.books.filter((b) => b.active);
  const [values, setValues] = useState(() =>
    Object.fromEntries(books.map((b) => [b.bookId, b.lastGivenOut]))
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const clamp = (b, v) => Math.max(b.from - 1, Math.min(b.to, v));
  const set = (b, v) => setValues((s) => ({ ...s, [b.bookId]: clamp(b, v) }));

  const save = async () => {
    setBusy(true);
    const res = await raffleApi.updateGivenOut({
      locationId,
      drawingId: drawing.drawingId,
      runId,
      books: books.map((b) => ({
        bookId: b.bookId,
        lastGivenOut: values[b.bookId],
      })),
    });
    setBusy(false);
    if (!res.success) return setError(res.message);
    onSaved(res);
  };

  return (
    <Sheet onClose={onClose}>
      <SheetTitle Icon={ConfirmationNumberIcon} onClose={onClose}>
        Tickets given out
      </SheetTitle>
      <div className="rf-muted" style={{ marginBottom: 16 }}>
        {drawing.name} · enter the last ticket number handed to a customer for
        each colour. Only these tickets go into the draw.
      </div>

      <div className="rf-col rf-gap-16" style={{ marginBottom: 20 }}>
        {books.map((b) => {
          const last = values[b.bookId];
          const count = Math.max(0, last - b.from + 1);
          return (
            <div key={b.bookId} className="rf-card">
              <div className="rf-row rf-gap-12" style={{ marginBottom: 12 }}>
                <TicketStub
                  number={last >= b.from ? `${b.from}–${last}` : 'none out'}
                  color={b.colorName}
                  size="sm"
                />
                <div className="rf-flex1 rf-minw0">
                  <div className="rf-xs rf-strong">
                    {count} tickets in the draw
                  </div>
                  <div className="rf-xs rf-muted">
                    Book: {b.from} to {b.to}
                  </div>
                </div>
              </div>
              <SectionLabel>Last ticket given out</SectionLabel>
              <Stepper
                value={last}
                min={b.from - 1}
                max={b.to}
                onChange={(v) => set(b, v)}
              />
              <div className="rf-row rf-gap-8" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="rf-soft-btn rf-flex1"
                  onClick={() => set(b, b.from - 1)}
                >
                  None given out
                </button>
                <button
                  type="button"
                  className="rf-soft-btn rf-flex1"
                  onClick={() => set(b, b.to)}
                >
                  Whole book given out
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginBottom: 16 }}>
        <Notice Icon={LockIcon}>
          You can update how many tickets went out. Changing the book range or
          the colour is manager only.
        </Notice>
      </div>
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="red">{error}</Notice>
        </div>
      )}
      <button
        type="button"
        className="rf-btn rf-btn-navy rf-w100"
        disabled={busy}
        onClick={save}
      >
        Save and continue
      </button>
    </Sheet>
  );
}
